"use client";
import React, { useState } from "react";
import hasCourseRunStarted from "@/lib/course/utils/hasCourseRunStarted";
import { LMS_COURSE_DOMAIN, LMS_DOMAIN } from "@/lib/course/utils/constants";
import { cn } from "@/lib/utils";
import { CourseEnrollLinkProps } from "@/components/CourseContent/EnrollButton/types";
import hasAuditSeat from "@/lib/course/utils/hasAuditSeat";
import useCourseEnrollment from "../hooks/useCourseEnrollment";
import { enrollButtonBaseStyles } from "./constants";
import EnterpriseNotificationModal from "@/components/CourseContent/ContentRefresh/EnterpriseNotificationModal";
import {
  ElementType,
  ElementClickedWebElement,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { getEnrollButtonText } from "@/lib/course/utils/getCourseRunEnrollButtonText";
import useWebview from "@/webview/useWebview";
import { useEnrollmentUrl } from "../hooks/useEnrollmentUrl";
import { useLogistrationModal } from "@/components/LogistrationModal/hooks/useLogistrationModal";
import useTranslations from "@/i18n/useTranslations";
import enMessages from "@/i18n/messages/en.json";

const courseEnAnalytics = enMessages.Course;

const baseStyles = `${enrollButtonBaseStyles} bg-brand text-white border-2 border-brand hover:bg-white hover:text-brand hover:border-brand`;

function EnrollLink({
  buttonClass = "",
  courseRun,
  course,
  name = "",
  location = "",
  ctaTextOverride,
  price,
  straightToCheckout,
  onClick,
}: CourseEnrollLinkProps) {
  const t = useTranslations("Course");
  const [isDisabled, setDisabled] = useState(false);
  const [isOpenEnterpriseModal, setIsOpenEnterpriseModal] = useState(false);
  const {
    isLoggedIn,
    isUserEnrolledInRun,
    isCourseEnrollable,
    handleEnrollment,
    commerceCoordinatorUrl,
    hasPurchasableSku,
    triggerConversionEvent,
    paidLeadValues,
    emailOptIn,
    isCourseArchived,
  } = useCourseEnrollment({
    courseRun,
    course,
  });
  // Only apply straightToCheckout for the primary hero CTA and side rail, not cert-module, highlights, sticky etc.
  const isHeroCta = location === "hero" || location === "side-rail";
  //  Verified-only courses (no audit seat) skip the LMS
  // Choose-Your-Track page in every CTA location, since there is no track
  // to choose. Single source of truth via `hasAuditSeat(course)`.
  const isVerifiedOnlyCourse = hasPurchasableSku && !hasAuditSeat(course);
  // hasPurchasableSku from useCourseEnrollment — derived from seat SKUs, auth-state agnostic.
  // Prevents audit-only courses from being sent to checkout.
  const shouldGoStraightToCheckout = hasPurchasableSku
    ? isVerifiedOnlyCourse || (isHeroCta && Boolean(straightToCheckout))
    : false;

  const { handleLogistrationModalClick, shouldShowModal } =
    useLogistrationModal();

  const { isWebview, getWebviewEnrollmentUrl, getWebviewDashboardUrl } =
    useWebview();

  //TODO: Replace with real API DATA
  const isEnterpriseUser = false;

  const { linkDirectToPaymentPage, enrollCtaLabel } = paidLeadValues || {};

  const { authUrl } = useEnrollmentUrl({
    courseRun,
    course,
    emailOptIn,
    commerceCoordinatorUrl,
    linkDirectToPaymentPage,
    isLoggedIn,
    straightToCheckout: shouldGoStraightToCheckout,
    isCourseEnrollable: isCourseEnrollable ?? false,
    isCourseArchived: isCourseArchived ?? false,
  });

  const courseStarted = courseRun.start
    ? hasCourseRunStarted(courseRun.start)
    : false;
  const courseUrl = courseStarted
    ? `${LMS_COURSE_DOMAIN}/course/${encodeURIComponent(courseRun.key)}/home`
    : `${LMS_DOMAIN}/dashboard`;

  const enrollButtonMessage = getEnrollButtonText(
    {
      enrollmentStart: courseRun.enrollmentStart,
      enrollmentEnd: courseRun.enrollmentEnd,
      end: courseRun.end,
    },
    isUserEnrolledInRun || false,
  );

  const enrollButtonText =
    ctaTextOverride ?? t(enrollButtonMessage.key, enrollButtonMessage.values);

  const ctaLabel =
    !ctaTextOverride &&
    enrollButtonMessage.key === "advanceYourCareer" &&
    enrollCtaLabel
      ? enrollCtaLabel
      : undefined;

  // Keep Tagular payloads in English (analytics identifiers)
  const webElementText =
    ctaLabel ??
    ctaTextOverride ??
    (enrollButtonMessage.key === "enrollAfter"
      ? `Enroll after ${enrollButtonMessage.values?.date ?? ""}`
      : courseEnAnalytics[enrollButtonMessage.key]);

  const displayText = ctaLabel ?? enrollButtonText;

  const sharedElementProps = {
    name,
    elementType: ElementType.Button,
    location,
    position: "1",
    text: webElementText,
  };

  /* Enroll button */
  const enrollWebElement: ElementClickedWebElement = {
    ...sharedElementProps,
    htmlId: "advance-free-cta",
  };

  // Specific to the certificate module
  const certWebElement: ElementClickedWebElement = {
    ...sharedElementProps,
    name: "course-conversion",
    htmlId: "earn-cert",
  };

  const { elementClicked: enrollElementClicked } = useElementEvents({
    webElement: enrollWebElement,
  });

  const { elementClicked: certElementClicked } = useElementEvents({
    webElement: certWebElement,
  });

  const fireEnrollEvent = () => {
    if (location.startsWith("cert-module")) {
      certElementClicked({ webElement: certWebElement });
    } else {
      enrollElementClicked({ webElement: enrollWebElement });
    }
  };

  const notifyClick = () => {
    onClick?.();
  };

  // Not logged in, enrollable
  const onClickAuth = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    if (!isDisabled) {
      if (shouldShowModal) {
        e.preventDefault();
        setDisabled(false);
        fireEnrollEvent();
        triggerConversionEvent();
        handleLogistrationModalClick(e, "register", onEnroll);
        notifyClick();
        return;
      } else {
        setDisabled(true);
        fireEnrollEvent();
        triggerConversionEvent();
        if (isWebview) {
          window.location.href = getWebviewEnrollmentUrl(
            courseRun.key,
            emailOptIn,
          );
        } else {
          if (window.newrelic) {
            window.newrelic.addPageAction("EnrollLink.onClickAuth", {
              authUrl,
            });
          }
          window.location.href = authUrl;
        }
        notifyClick();
      }
    }
  };

  // Logged in, enrollable
  const onClickEnroll = () => {
    if (!isDisabled) {
      setDisabled(true);
      fireEnrollEvent();
      onEnroll();
      notifyClick();
    }
  };

  const onEnroll = () => {
    if (isEnterpriseUser) {
      setIsOpenEnterpriseModal(true);
    } else {
      if (isWebview) {
        window.location.href = getWebviewEnrollmentUrl(
          courseRun.key,
          emailOptIn,
        );
      } else {
        if (window.newrelic) {
          window.newrelic.addPageAction("EnrollLink.onClickEnroll", {
            isLoggedIn,
          });
        }
        handleEnrollment(undefined, shouldGoStraightToCheckout);
      }
    }
  };

  /* Already enrolled */
  const alreadyEnrolledWebElement: ElementClickedWebElement = {
    ...sharedElementProps,
    htmlId: "enrolled-course",
    text: webElementText,
  };

  const { elementClicked: alreadyEnrolledElementClicked } = useElementEvents({
    webElement: alreadyEnrolledWebElement,
  });

  const onClickAlreadyEnrolled = () => {
    if (!isDisabled) {
      setDisabled(true);
      triggerConversionEvent();
      alreadyEnrolledElementClicked({ webElement: alreadyEnrolledWebElement });
      if (isWebview) {
        window.location.href = getWebviewDashboardUrl(courseRun.key);
      } else {
        window.location.href = courseUrl;
      }
      notifyClick();
    }
  };

  return (
    <>
      {!isLoggedIn && isCourseEnrollable && (
        <button
          type="button"
          className={cn(baseStyles, buttonClass)}
          onClick={(e) => onClickAuth(e)}
          disabled={isDisabled}
          id={courseRun.key}
        >
          {displayText}
        </button>
      )}
      {isLoggedIn && !isUserEnrolledInRun && isCourseEnrollable && (
        <button
          type="button"
          className={cn(baseStyles, buttonClass)}
          onClick={onClickEnroll}
          disabled={isDisabled}
          id={courseRun.key}
        >
          {displayText}
        </button>
      )}
      {isLoggedIn && isUserEnrolledInRun && (
        <button
          type="button"
          className={cn(baseStyles, buttonClass)}
          onClick={onClickAlreadyEnrolled}
          disabled={isDisabled}
          id={courseRun.key}
        >
          {displayText}
        </button>
      )}
      <EnterpriseNotificationModal
        closeModal={() => {
          setIsOpenEnterpriseModal(false);
          setDisabled(false);
        }}
        isOpen={isOpenEnterpriseModal}
        onContinue={onClickEnroll}
      />
    </>
  );
}

export default EnrollLink;
